import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PermissionsService } from '../../global-services/permissions.service';
import { Permission, UserOrgPermission } from '../../global-data-models/permission.model';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { UserService } from '../../global-services/user.service';

export interface CrudOrgPermissionsUiState {
  users: {
    userId: string;
    name: string;
    email: string;
    permissions: number[];
    form: FormGroup;
  }[];
  orgLevelPermissions: Permission[];
  error?: string;
}

export type CrudOrgPermissionsMode = 'read' | 'update';

@Injectable({ providedIn: 'root' })
export class CrudOrgPermissionsFacade {
  private uiStateSubject = new BehaviorSubject<CrudOrgPermissionsUiState>({
    users: [],
    orgLevelPermissions: [],
  });
  uiState$ = this.uiStateSubject.asObservable();
  private orgId: string | null = null;
  private mode: CrudOrgPermissionsMode = 'read';

  constructor(
    private permissionsService: PermissionsService,
    private fb: FormBuilder,
    private messageService: GlobalMessageService,
    private loading: GlobalLoadingService,
    private userService: UserService,
  ) {}

  async load(orgId: string, mode: CrudOrgPermissionsMode = 'read') {
    try {
      this.loading.show();
      this.orgId = orgId;
      this.mode = mode;
      this.uiStateSubject.next({ ...this.uiStateSubject.value });

      const orgLevelPermissions = await this.permissionsService.getAllOrgLevelPermissions();
      const usersMap = await this.permissionsService.getOupByUsers(orgId);

      // Add a form group for each user to set the permissions
      const users = Array.from(usersMap.values()).map((u) => {
        const form = this.fb.group({
          permissions: this.fb.control({ value: u.permissions, disabled: mode === 'read' }),
        });
        return {
          ...u,
          form,
        };
      });

      this.uiStateSubject.next({ users: users, orgLevelPermissions: orgLevelPermissions });
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to load permissions');
    } finally {
      this.loading.hide();
    }
  }

  async updateUserPermissions(userId: string, form: FormGroup) {
    try {
      this.loading.show();

      if (!this.orgId) return;
      if (this.mode === 'read') return;
      const permissionIdsRequested = form.value.permissions;

      //Check if the user has any of these permissions already
      const existingPermissionsIds = await this.permissionsService.getIndividualUserOrgPermissions(this.orgId, userId);
      const permissionsToRemove = existingPermissionsIds.filter((id) => !permissionIdsRequested.includes(id));
      const permissionsToAdd = permissionIdsRequested.filter((id: number) => !existingPermissionsIds.includes(id));

      if (permissionsToRemove.length === 0 && permissionsToAdd.length === 0) {
        this.messageService.showInfo('No changes', 'No changes to permissions');
        return;
      }

      for (const permissionId of permissionsToRemove) {
        await this.permissionsService.deleteUserOrgPermission(this.orgId, userId, permissionId);
      }

      for (const permissionId of permissionsToAdd) {
        await this.permissionsService.createUserOrgPermission(this.orgId, userId, permissionId);
      }

      await this.load(this.orgId, this.mode);
      this.messageService.showSuccess('Updated', 'Permissions updated');
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to update permissions');
    } finally {
      this.loading.hide();
    }
  }

  async deleteUser(userId: string) {
    try {
      this.loading.show();

      if (!this.orgId) return;
      if (this.mode === 'read') return;

      await this.userService.orgAdminDeleteUser(userId, this.orgId);

      // Reload the data after successful deletion
      await this.load(this.orgId, this.mode);
      this.messageService.showSuccess('Deleted', 'User successfully deleted');
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'Unknown error during deletion';
      this.messageService.showError('Save Failed', message);
    } finally {
      this.loading.hide();
    }
  }
}
